import { useState } from 'react'
import { XMarkIcon, TrashIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { formatDateTime } from '../../utils/dateUtils'

const VALID_STATUSES = ['new', 'contacted', 'qualified', 'converted', 'lost']

const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700',
  contacted: 'bg-amber-50 text-amber-700',
  qualified: 'bg-purple-50 text-purple-700',
  converted: 'bg-green-50 text-green-700',
  lost: 'bg-red-50 text-red-700',
}

export default function LeadDetail({ lead, onClose, onUpdate, onDelete }) {
  const [status, setStatus] = useState(lead.status || 'new')
  const [notes, setNotes] = useState(lead.notes || '')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await onUpdate(lead.id, { status, notes })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await onDelete(lead.id)
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-slate-900/50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-auto p-6 text-left z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold text-slate-900">Lead Details</h3>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Contact Info */}
          <div className="space-y-3 mb-6">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
                <div className="text-sm text-slate-900">{lead.name || '—'}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                <div className="text-sm text-slate-900">{lead.email || '—'}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                <div className="text-sm text-slate-900">{lead.phone || '—'}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Company</label>
                <div className="text-sm text-slate-900">{lead.company || '—'}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Source</label>
                <div className="text-sm text-slate-600">{lead.source || '—'}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Created</label>
                <div className="text-sm text-slate-600">{formatDateTime(lead.created_at)}</div>
              </div>
            </div>

            {lead.page_url && (
              <div className="pt-2 border-t border-slate-100">
                <label className="block text-xs font-medium text-slate-500 mb-1">Page URL</label>
                <a href={lead.page_url} target="_blank" rel="noopener noreferrer" className="text-sm text-brand-600 hover:underline truncate block">
                  {lead.page_url}
                </a>
              </div>
            )}

            {lead.session_id && (
              <div className="pt-2 border-t border-slate-100">
                <button className="flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700">
                  <ChatBubbleLeftRightIcon className="h-4 w-4" />
                  View Conversation
                </button>
              </div>
            )}
          </div>

          {/* Status Dropdown */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              {VALID_STATUSES.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add notes about this lead..."
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <TrashIcon className="h-4 w-4" />
              Delete
            </button>

            <div className="flex items-center gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

          {/* Delete Confirmation */}
          {showDeleteConfirm && (
            <div className="absolute inset-0 bg-white/95 rounded-2xl flex items-center justify-center p-6 z-10">
              <div className="text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
                <h4 className="text-base font-semibold text-slate-900 mb-2">Delete Lead?</h4>
                <p className="text-sm text-slate-500 mb-4">This action cannot be undone.</p>
                <div className="flex items-center justify-center gap-2">
                  <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
